import React from 'react';
import { motion } from 'framer-motion';
import { Search, FlaskConical, Rocket, LineChart } from 'lucide-react';
export function Process() {
  const steps = [{
    number: '01',
    icon: Search,
    title: 'Assessment & Strategy',
    description: 'We assess the current application landscape, architecture, code quality, technical debt, risks, and business priorities to define the optimal modernization strategy and roadmap.'
  }, {
    number: '02',
    icon: FlaskConical,
    title: 'Proof of Concept (POC)',
    description: 'We validate the proposed approach through a focused proof of concept to demonstrate feasibility, measure outcomes, reduce uncertainty, and align stakeholders before large-scale execution.'
  }, {
    number: '03',
    icon: Rocket,
    title: 'Execution',
    description: 'Using AI-powered tools, engineering accelerators, and disciplined delivery practices, we execute migrations, upgrades, refactoring, testing, and modernization initiatives at scale.'
  }, {
    number: '04',
    icon: LineChart,
    title: 'Review & Optimization',
    description: 'We continuously review quality, performance, security, maintainability, and delivery outcomes to optimize the platform and ensure long-term engineering sustainability.'
  }];
  return <section id="process" className="relative py-24 bg-slate-50 overflow-hidden" data-id="element-223">
      {/* Subtle background accents */}
      <div className="absolute inset-0 pointer-events-none" data-id="element-224">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-brand-green/5 rounded-full blur-3xl" data-id="element-225" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-brand-blue/5 rounded-full blur-3xl" data-id="element-226" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-227">
        {/* Header */}
        <div className="max-w-3xl mb-20" data-id="element-228">
          <motion.div initial={{
          opacity: 0,
          y: 12
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} data-id="element-229">
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-brand-green/10 text-brand-green text-xs font-semibold uppercase tracking-wider mb-5" data-id="element-230">
              <span className="w-1.5 h-1.5 rounded-full bg-brand-green" data-id="element-231" />
              How We Work
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6 font-heading leading-tight" data-id="element-232">
              Our Modernization Process
            </h2>
            <p className="text-lg text-slate-600 leading-relaxed" data-id="element-233">
              A structured, AI-assisted modernization approach designed to
              reduce risk, improve visibility, and accelerate transformation
              outcomes.
            </p>
          </motion.div>
        </div>

        {/* Timeline */}
        <div className="relative" data-id="element-234">
          {/* Horizontal connecting line — desktop only */}
          <div className="hidden lg:block absolute top-[88px] left-0 right-0 h-px" data-id="element-235">
            <div className="h-full bg-gradient-to-r from-transparent via-slate-300 to-transparent" data-id="element-236" />
            <div className="absolute inset-0 flex justify-between px-[88px]" data-id="element-237">
              {[...Array(3)].map((_, i) => <div key={i} className="flex-1 flex justify-center" data-id="element-238">
                  <div className="w-2 h-2 rounded-full bg-brand-green/40 -translate-y-[3px]" data-id="element-239" />
                </div>)}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-6" data-id="element-240">
            {steps.map((step, index) => {
            const Icon = step.icon;
            return <motion.div key={step.number} initial={{
              opacity: 0,
              y: 24
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true,
              margin: '-50px'
            }} transition={{
              duration: 0.5,
              delay: index * 0.12
            }} className="group relative" data-id="element-241">
                  {/* Step number badge sitting above the card */}
                  <div className="relative z-10 mb-6 flex items-start" data-id="element-242">
                    <div className="relative" data-id="element-243">
                      <div className="w-44 h-44 rounded-2xl bg-white shadow-lg shadow-slate-200/60 border border-slate-100 flex flex-col items-center justify-center transition-all duration-300 group-hover:shadow-xl group-hover:-translate-y-1 group-hover:border-brand-green/30" data-id="element-244">
                        {/* Big faded watermark number behind the icon */}
                        <span className="absolute inset-0 flex items-center justify-center font-heading font-bold text-[7rem] leading-none text-slate-100 select-none pointer-events-none" aria-hidden="true" data-id="element-245">
                          {step.number}
                        </span>

                        {/* Icon */}
                        <div className="relative z-10 w-16 h-16 rounded-xl bg-brand-navy flex items-center justify-center shadow-md shadow-brand-navy/20" data-id="element-246">
                          <Icon className="w-7 h-7 text-white" strokeWidth={2} data-id="element-247" />
                        </div>

                        {/* Step indicator chip */}
                        <span className="relative z-10 mt-4 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-brand-green/10 text-brand-green text-[10px] font-bold tracking-wider" data-id="element-248">
                          STEP {step.number}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Text content below the card */}
                  <div className="px-1" data-id="element-249">
                    <h3 className="text-xl font-bold text-slate-900 mb-3 font-heading group-hover:text-brand-navy transition-colors" data-id="element-250">
                      {step.title}
                    </h3>
                    <p className="text-slate-600 text-sm leading-relaxed" data-id="element-251">
                      {step.description}
                    </p>
                  </div>
                </motion.div>;
          })}
          </div>
        </div>
      </div>
    </section>;
}